import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle, Building, Landmark, ShoppingCart, Megaphone, Briefcase } from "lucide-react";

const SECTORS = {
  "agences-immobilieres": {
    icon: Landmark, title: "SaaS pour Agences Immobilieres",
    subtitle: "Gerez vos mandats, acquéreurs et vitrines depuis une plateforme unique",
    metaTitle: "Logiciel gestion agence immobiliere | GLOTECH OS",
    metaDesc: "Plateforme digitale tout-en-un pour agences immobilieres. CRM, mandats, vitrines, facturation et marketing.",
    intro: "GLOTECH OS offre aux agences immobilieres une solution complete pour gerer leur portefeuille de biens, suivre les acquéreurs et automatiser leur marketing digital.",
    features: [
      "CRM acquéreurs et vendeurs avec suivi des interactions",
      "Gestion des mandats et contrats avec signature electronique",
      "Facturation automatisee et paiements en ligne",
      "Vitrine web optimisee SEO pour chaque agence",
      "Publication automatique des biens sur les reseaux sociaux",
      "Dashboard de performance avec KPIs immobiliers",
      "Suivi SEO Google Analytics et Search Console",
      "Assistant IA pour la redaction d'annonces",
    ],
  },
  "e-commerce": {
    icon: ShoppingCart, title: "SaaS pour E-commerce",
    subtitle: "Analytics avances, marketing automatise et fidelisation client",
    metaTitle: "Plateforme marketing e-commerce | GLOTECH OS",
    metaDesc: "Solution tout-en-un pour e-commerce. Analytics, SEO, reseaux sociaux et automatisation marketing.",
    intro: "GLOTECH OS aide les entreprises e-commerce a optimiser leur presence en ligne, analyser leurs performances et automatiser leur marketing.",
    features: [
      "Dashboard analytics complet avec KPIs e-commerce",
      "Suivi SEO et positions Google en temps reel",
      "Surveillance des concurrents et analyse de marche",
      "Gestion des campagnes reseaux sociaux multi-plateformes",
      "Generation de contenu IA pour fiches produits et blog",
      "Facturation et paiements Stripe integres",
      "Automatisation des campagnes marketing",
      "CRM client avec segmentation avancee",
    ],
  },
  "agences-marketing": {
    icon: Megaphone, title: "SaaS pour Agences Marketing",
    subtitle: "Gestion multi-clients, reporting automatise et campagnes centralisees",
    metaTitle: "Logiciel gestion agence marketing | GLOTECH OS",
    metaDesc: "Plateforme SaaS pour agences marketing et communication. Multi-clients, reporting, SEO et reseaux sociaux.",
    intro: "GLOTECH OS est la solution ideale pour les agences marketing qui gerent plusieurs clients et ont besoin d'un outil centralise pour le reporting, le SEO et les reseaux sociaux.",
    features: [
      "Gestion multi-clients avec tableau de bord par client",
      "Reporting SEO automatise (Google Analytics + Search Console)",
      "Planification et publication reseaux sociaux pour chaque client",
      "Generation de contenu IA (articles, posts, series)",
      "Devis et facturation par projet ou abonnement",
      "Surveillance concurrents pour chaque client",
      "Workflow complet : lead → devis → contrat → projet",
      "Architecture multi-agences en marque blanche",
    ],
  },
  "consultants": {
    icon: Briefcase, title: "SaaS pour Consultants & Services",
    subtitle: "Propositions commerciales, contrats et suivi de missions",
    metaTitle: "Logiciel gestion consultant freelance | GLOTECH OS",
    metaDesc: "Plateforme digitale pour consultants et prestataires de services. Devis, contrats, facturation et marketing.",
    intro: "GLOTECH OS accompagne les consultants et prestataires de services dans la gestion de leur activite, de la prospection a la facturation.",
    features: [
      "Propositions commerciales professionnelles en PDF",
      "Signature electronique des contrats",
      "Facturation automatique et paiements en ligne",
      "CRM pour le suivi des prospects et clients",
      "Site vitrine et blog SEO pour la visibilite",
      "Assistant IA pour la redaction de contenus",
      "Dashboard de performance avec suivi du chiffre d'affaires",
      "Gestion des affilies et recommandations",
    ],
  },
};

export default function SectorPage() {
  const { sectorSlug } = useParams();
  const sector = SECTORS[sectorSlug];

  if (!sector) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h1 className="text-3xl font-bold mb-4">Secteur introuvable</h1>
        <Link to="/" className="text-[#0099CC] hover:underline">Retour a l'accueil</Link>
      </div>
    );
  }

  const Icon = sector.icon;

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-12">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-14 w-14 rounded-2xl bg-[#0099CC]/10 flex items-center justify-center">
            <Icon className="h-7 w-7 text-[#0099CC]" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold">{sector.title}</h1>
            <p className="text-[#94A3B8] mt-1">{sector.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-[#94A3B8] leading-relaxed text-lg mb-8">{sector.intro}</p>
            <h2 className="text-xl font-bold text-white mb-6">Fonctionnalites cles</h2>
            <div className="space-y-3">
              {sector.features.map(f => (
                <div key={f} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  <span className="text-[#94A3B8] text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 flex flex-col items-center justify-center text-center">
            <Icon className="h-20 w-20 text-[#0099CC]/30 mb-6" />
            <h3 className="text-white font-bold text-xl mb-3">Pret a demarrer ?</h3>
            <p className="text-[#64748b] text-sm mb-6 max-w-xs">Demandez une demonstration personnalisee pour votre secteur d'activite.</p>
            <Link to="/devis" className="inline-flex items-center gap-2 bg-[#0099CC] hover:bg-[#0088B5] text-white px-6 py-3 rounded-xl font-semibold transition-all">
              Demander une demo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Other sectors */}
      <section className="bg-[#080C14] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center mb-10">Decouvrez aussi</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(SECTORS).filter(([k]) => k !== sectorSlug).map(([k, s]) => (
              <Link key={k} to={`/secteurs/${k}`} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4 text-center hover:border-[#0099CC]/30 transition-all">
                <s.icon className="h-6 w-6 text-[#0099CC] mx-auto mb-2" />
                <p className="text-white text-sm font-medium">{s.title.replace("SaaS pour ", "")}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
